import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Field, Input, SubmitButton, usePageMeta } from "@/components/shared";
import { Reveal } from "@/components/motion-primitives";

export default function OwnerLogin() {
  usePageMeta("Owner Login", "Secure login for Bermuda Co-Host property owners.");
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) {
    navigate("/owner", { replace: true });
    return null;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(form.email, form.password);
      toast.success("Welcome back.");
      navigate("/owner", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-navy px-6 py-32 text-ivory">
      <Reveal className="w-full max-w-md">
        <div className="border border-white/10 bg-navy-light/60 p-9 backdrop-blur-xl lg:p-11" data-testid="owner-login-card">
          <div className="flex flex-col items-center text-center">
            <img src="/logo-stacked-white.png" alt="The Bermuda Co-Host" className="h-20 w-auto" />
            <p className="mt-4 text-[10px] uppercase tracking-[0.25em] text-ivory/50">Owner Portal</p>
          </div>

          <form onSubmit={onSubmit} className="dark-form mt-9 grid gap-6" data-testid="owner-login-form">
            <Field label="Email" required>
              <Input data-testid="owner-login-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-white/20 bg-navy px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/35 outline-none focus:border-bermuda" placeholder="you@example.com" />
            </Field>
            <Field label="Password" required>
              <Input data-testid="owner-login-password" required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full border border-white/20 bg-navy px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/35 outline-none focus:border-bermuda" placeholder="••••••••" />
            </Field>
            {error && <p data-testid="owner-login-error" className="text-sm text-bermuda">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="owner-portal-login-btn"
              className="w-full bg-bermuda px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-ivory disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <div className="mt-8 border-t border-white/10 pt-6">
            <p className="text-xs leading-relaxed text-ivory/50">
              Demo access: <span className="font-mono text-ivory/75">owner@bermudacohost.demo</span> /{" "}
              <span className="font-mono text-ivory/75">demo1234</span>
            </p>
            <p className="mt-3 text-xs leading-relaxed text-ivory/50">
              Not yet a client?{" "}
              <Link to="/assessment" data-testid="owner-login-assessment-link" className="text-bermuda underline underline-offset-4 hover:text-ivory">
                Request a property assessment
              </Link>{" "}
              to begin.
            </p>
          </div>
        </div>
      </Reveal>
    </main>
  );
}
